import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

// gloss
const hstackVariants = cva('flex flex-row', {
  variants: {
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      6: 'gap-6',
      8: 'gap-8',
      12: 'gap-12',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    wrap: {
      true: 'flex-wrap',
      false: 'flex-nowrap',
    },
    mdDirection: {
      row: 'md:flex-row',
      col: 'md:flex-col',
    },
    mdAlign: {
      start: 'md:items-start',
      center: 'md:items-center',
      end: 'md:items-end',
      stretch: 'md:items-stretch',
      baseline: 'md:items-baseline',
    },
    mdJustify: {
      start: 'md:justify-start',
      center: 'md:justify-center',
      end: 'md:justify-end',
      between: 'md:justify-between',
    },
    mdGap: {
      0: 'md:gap-0',
      1: 'md:gap-1',
      2: 'md:gap-2',
      3: 'md:gap-3',
      4: 'md:gap-4',
      6: 'md:gap-6',
      8: 'md:gap-8',
      12: 'md:gap-12',
    },
  },
  defaultVariants: {
    gap: 2,
    align: 'center',
    wrap: false,
  },
});

// gloss
type HStackProps = useRender.ComponentProps<'div'> & VariantProps<typeof hstackVariants>;

const HStack = ({
  className,
  gap = 2,
  align = 'center',
  justify,
  wrap = false,
  mdDirection,
  mdAlign,
  mdJustify,
  mdGap,
  render,
  ...props
}: HStackProps) => {
  return useRender({
    defaultTagName: 'div',
    props: mergeProps<'div'>(
      {
        className: cn(hstackVariants({ gap, align, justify, wrap, mdDirection, mdAlign, mdJustify, mdGap, className })),
      },
      props,
    ),
    render,
    state: {
      slot: 'hstack',
    },
  });
};

export { HStack, hstackVariants };
